import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../hooks/useTheme';
import StaggerReveal from '../../../components/feature/StaggerReveal';

interface EditionDef {
  icon: string;
  accentColor: string;
  edition: string; // brand suffix, e.g. "Market" → InnoFlex Market
  industryKey: string;
  planKey: string;
  descKey: string;
}

const FAMILY: EditionDef[] = [
  { icon: 'ri-shopping-cart-2-line', accentColor: '#10b981', edition: 'Market',      industryKey: 'fam_1_industry',  planKey: 'fam_1_plan',  descKey: 'fam_1_desc' },
  { icon: 'ri-restaurant-2-line',    accentColor: '#f59e0b', edition: 'Restaurant',  industryKey: 'fam_2_industry',  planKey: 'fam_2_plan',  descKey: 'fam_2_desc' },
  { icon: 'ri-health-book-line',     accentColor: '#a855f7', edition: 'Dental',      industryKey: 'fam_3_industry',  planKey: 'fam_3_plan',  descKey: 'fam_3_desc' },
  { icon: 'ri-stethoscope-line',     accentColor: '#f43f5e', edition: 'Clinic',      industryKey: 'fam_13_industry', planKey: 'fam_13_plan', descKey: 'fam_13_desc' },
  { icon: 'ri-truck-line',           accentColor: '#06b6d4', edition: 'Distribution',industryKey: 'fam_4_industry',  planKey: 'fam_4_plan',  descKey: 'fam_4_desc' },
  { icon: 'ri-hammer-line',          accentColor: '#f97316', edition: 'Hardware',    industryKey: 'fam_5_industry',  planKey: 'fam_5_plan',  descKey: 'fam_5_desc' },
  { icon: 'ri-car-line',             accentColor: '#3b82f6', edition: 'Rent-a-Car',  industryKey: 'fam_6_industry',  planKey: 'fam_6_plan',  descKey: 'fam_6_desc' },
  { icon: 'ri-community-line',       accentColor: '#14b8a6', edition: 'Condo',       industryKey: 'fam_7_industry',  planKey: 'fam_7_plan',  descKey: 'fam_7_desc' },
  { icon: 'ri-settings-3-line',      accentColor: '#0ea5e9', edition: 'Auto Parts',  industryKey: 'fam_8_industry',  planKey: 'fam_8_plan',  descKey: 'fam_8_desc' },
  { icon: 'ri-bank-line',            accentColor: '#6366f1', edition: 'Finance',     industryKey: 'fam_9_industry',  planKey: 'fam_9_plan',  descKey: 'fam_9_desc' },
  { icon: 'ri-run-line',             accentColor: '#ec4899', edition: 'Fitness',     industryKey: 'fam_10_industry', planKey: 'fam_10_plan', descKey: 'fam_10_desc' },
  { icon: 'ri-tools-line',           accentColor: '#8b5cf6', edition: 'Services',    industryKey: 'fam_11_industry', planKey: 'fam_11_plan', descKey: 'fam_11_desc' },
  { icon: 'ri-t-shirt-line',         accentColor: '#22d3ee', edition: 'Retail',      industryKey: 'fam_12_industry', planKey: 'fam_12_plan', descKey: 'fam_12_desc' },
];

function EditionCard({ item }: { item: EditionDef }) {
  const [hovered, setHovered] = useState(false);
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const { t } = useTranslation();
  const accent = item.accentColor;

  const textSub = isLight ? 'rgba(10,22,40,0.60)' : 'rgba(255,255,255,0.55)';
  const brandMuted = isLight ? 'rgba(10,22,40,0.45)' : 'rgba(255,255,255,0.45)';

  return (
    <div
      className="group relative rounded-2xl border p-5 md:p-6 h-full flex flex-col cursor-default overflow-hidden"
      style={{
        borderColor: hovered
          ? accent + '60'
          : isLight ? 'rgba(10,22,40,0.10)' : 'rgba(255,255,255,0.07)',
        background: isLight
          ? hovered ? `linear-gradient(150deg, ${accent}0d 0%, #ffffff 70%)` : '#ffffff'
          : hovered ? `linear-gradient(150deg, ${accent}14 0%, rgba(10,20,40,0.96) 70%)` : 'rgba(10,20,40,0.6)',
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        boxShadow: hovered
          ? isLight ? `0 14px 40px ${accent}20` : `0 16px 44px rgba(0,0,0,0.45), 0 0 26px ${accent}1a`
          : isLight ? '0 2px 10px rgba(0,0,0,0.06)' : 'none',
        transition: 'transform 0.35s cubic-bezier(0.34,1.4,0.64,1), border-color 0.3s ease, box-shadow 0.3s ease, background 0.4s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Top accent bar */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: `linear-gradient(to right, ${accent}, ${accent}00)`, opacity: hovered ? 1 : 0.45 }}
      />

      {/* Icon */}
      <div
        className="w-12 h-12 flex items-center justify-center rounded-xl mb-4 flex-shrink-0"
        style={{
          backgroundColor: accent + '18',
          color: accent,
          transform: hovered ? 'scale(1.08) rotate(-4deg)' : 'scale(1) rotate(0deg)',
          transition: 'transform 0.35s cubic-bezier(0.34,1.56,0.64,1)',
        }}
      >
        <i className={`${item.icon} text-2xl`}></i>
      </div>

      {/* Edition name (brand) */}
      <h3 className="font-heading text-lg font-bold leading-snug">
        <span style={{ color: brandMuted, fontWeight: 600 }}>InnoFlex </span>
        <span style={{ color: accent }}>{item.edition}</span>
      </h3>

      {/* Industry served */}
      <p className="font-body text-xs mt-0.5 mb-3" style={{ color: textSub }}>
        {t(item.industryKey)}
      </p>

      {/* Recommended plan */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span
          className="font-body text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: isLight ? 'rgba(10,22,40,0.42)' : 'rgba(255,255,255,0.40)' }}
        >
          {t('industries_rec')}
        </span>
        <span
          className="inline-flex items-center gap-1.5 font-body text-[11px] font-bold px-2.5 py-1 rounded-full"
          style={{ backgroundColor: accent + '18', border: `1px solid ${accent}3d`, color: accent }}
        >
          <i className="ri-award-line text-xs"></i>
          {t(item.planKey)}
        </span>
      </div>

      {/* Description */}
      <p className="font-body text-sm leading-relaxed" style={{ color: isLight ? 'rgba(10,22,40,0.72)' : 'rgba(255,255,255,0.62)' }}>
        {t(item.descKey)}
      </p>
    </div>
  );
}

export default function Industries() {
  const { t } = useTranslation();

  return (
    <section id="industries" className="bg-navy-950 py-12 md:py-24 px-4 sm:px-5 md:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block text-electric text-xs md:text-sm font-semibold tracking-widest uppercase font-body mb-2 md:mb-3">{t('industries_label')}</span>
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4">
            {t('industries_title_1')} <span className="text-electric">{t('industries_title_accent')}</span>
          </h2>
          <p className="font-body text-white/55 text-sm md:text-base max-w-2xl mx-auto">
            {t('industries_sub')}
          </p>
        </div>
        <StaggerReveal
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 items-stretch"
          staggerDelay={70}
        >
          {FAMILY.map((item) => (
            <EditionCard key={item.edition} item={item} />
          ))}
        </StaggerReveal>

        {/* All industries note */}
        <div className="flex justify-center mt-8 md:mt-10">
          <div className="inline-flex items-center gap-2.5 rounded-full border border-electric/25 bg-electric/10 px-5 py-2.5 max-w-2xl">
            <div className="w-4 h-4 flex items-center justify-center text-electric flex-shrink-0">
              <i className="ri-apps-2-line text-sm"></i>
            </div>
            <span className="font-body text-xs md:text-sm font-medium text-white/70 text-center leading-snug">
              {t('industries_all')}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
